'use client';

import { useMemo, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { AlertManager } from '@dealroadshow/uikit-alert';
import TimeZoneRepository from '@framework/timezone/TimeZoneRepository';
import { ITimezone } from '@framework/timezone/vo/Timezone';
import { getDefaultTimeZone, getTimeZoneOption } from '@framework/timezone/helpers';

export const useTimeZone = (getListFn?: () => Promise<ITimezone[]>, initialTimeZoneList: ITimezone[] = []) => {
  const container = getContainer();
  const timeZoneRepository = container.get<TimeZoneRepository>(TimeZoneRepository);

  const [timeZoneList, setTimeZoneList] = useState<ITimezone[]>(initialTimeZoneList);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetched, setIsFetched] = useState(!!initialTimeZoneList.length);

  const timeZoneOptions = useMemo(() => {
    return timeZoneList.map(getTimeZoneOption);
  }, [timeZoneList]);

  const defaultTimeZone = useMemo(() => {
    return getDefaultTimeZone(timeZoneList);
  }, [timeZoneList]);

  const getTimeZoneList = async ({
    shouldHandleError = true,
  }: {
    /**
     * Determines whether the error needs to be handled
     * If not, the error will be thrown
     * By default: true
     */
    shouldHandleError?: boolean,
  } = {}): Promise<ITimezone[]> => {
    if (isFetched) {
      return timeZoneList;
    }

    setIsFetching(true);

    try {
      const fetchFn = getListFn || (() => timeZoneRepository.getList());
      const timeZoneList = await fetchFn();
      setTimeZoneList(timeZoneList);
      setIsFetched(true);

      return timeZoneList;
    } catch (error) {
      if (shouldHandleError) {
        AlertManager.error(getErrorMessage(error));
        return [];
      }

      throw error;
    } finally {
      setIsFetching(false);
    }
  };

  return {
    getTimeZoneList,
    defaultTimeZone,
    timeZoneList,
    timeZoneOptions,
    isFetching,
    isFetched,
  };
};
